(() => {
  if (window.__tpAnalyticsV3) return;
  window.__tpAnalyticsV3 = true;

  const CONFIG = Object.assign({
    tp_theme_version: 'v4',
    tp_page_type: '',
    tp_template: '',
    tp_customer_type: 'guest',
    tp_currency: 'EUR',
    tp_preview: (() => {
      try { return new URLSearchParams(location.search).has('preview_theme_id'); }
      catch (_) { return false; }
    })()
  }, window.TP_ANALYTICS_CONTEXT || {});

  const BLOCKED_KEY = /(email|e-mail|phone|telefon|mobile|name|first_name|last_name|address|street|city|postal|postcode|zip|plz|customer_id|order_id|href|url|query|search|message|note|comment|body)/i;
  const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
  const PHONE_PATTERN = /(?:\+?\d[\d\s().\/-]{6,}\d)/;
  const URL_PATTERN = /(?:https?:\/\/|mailto:|tel:)/i;
  const recentEvents = new Map();
  const selfDataLayerObjects = new WeakSet();
  const plannerState = new WeakMap();
  const b2bState = new WeakMap();

  const debugEnabled = (() => {
    try { return new URLSearchParams(location.search).get('tp_analytics_debug') === '1'; }
    catch (_) { return false; }
  })();

  const normalizeString = (value, max = 100) => String(value == null ? '' : value).replace(/\s+/g, ' ').trim().slice(0, max);
  const safeValue = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
    if (value == null) return undefined;
    const text = normalizeString(value);
    if (!text || EMAIL_PATTERN.test(text) || PHONE_PATTERN.test(text) || URL_PATTERN.test(text)) return undefined;
    return text;
  };
  const sanitize = (payload = {}) => {
    const output = {};
    Object.entries(payload).forEach(([key, value]) => {
      const cleanKey = normalizeString(key, 40).replace(/[^a-zA-Z0-9_]/g, '_');
      if (!cleanKey || BLOCKED_KEY.test(cleanKey)) return;
      const cleanValue = safeValue(value);
      if (cleanValue !== undefined) output[cleanKey] = cleanValue;
    });
    return output;
  };
  const analyticsAllowed = () => {
    try {
      const fn = window.Shopify?.customerPrivacy?.analyticsProcessingAllowed;
      return typeof fn === 'function' ? fn.call(window.Shopify.customerPrivacy) === true : false;
    } catch (_) { return false; }
  };
  const eventSignature = (name, payload) => `${name}|${JSON.stringify(payload)}`;
  const isDuplicate = (name, payload, windowMs = 900) => {
    const signature = eventSignature(name, payload), now = Date.now(), previous = recentEvents.get(signature) || 0;
    recentEvents.set(signature, now);
    if (recentEvents.size > 250) {
      const cutoff = now - 5000;
      recentEvents.forEach((timestamp, key) => { if (timestamp < cutoff) recentEvents.delete(key); });
    }
    return now - previous < windowMs;
  };
  const publishShopify = (name, payload) => {
    try {
      const publish = window.Shopify?.analytics?.publish;
      if (typeof publish === 'function') {
        const result = publish.call(window.Shopify.analytics, name, payload);
        if (result?.catch) result.catch(() => {});
        return true;
      }
    } catch (_) {}
    return false;
  };
  const publishGoogle = (name, payload) => {
    if (!analyticsAllowed()) return false;
    try {
      if (typeof window.gtag === 'function' && !window.google_tag_manager) {
        window.gtag('event', name, payload);
        return true;
      }
      if (Array.isArray(window.dataLayer)) {
        const item = { event: name, ...payload };
        selfDataLayerObjects.add(item);
        window.dataLayer.push(item);
        return true;
      }
    } catch (_) {}
    return false;
  };
  const track = (name, payload = {}, options = {}) => {
    if (!/^tp_[a-z0-9_]{1,36}$/i.test(name)) return false;
    const clean = sanitize({ ...CONFIG, ...payload });
    if (!options.allowRepeat && isDuplicate(name, clean, options.dedupeMs || 900)) return false;
    const shopifyPublished = publishShopify(name, clean);
    const googlePublished = options.skipGoogle ? false : publishGoogle(name, clean);
    try { window.dispatchEvent(new CustomEvent('tp:analytics', { detail: { name, payload: clean } })); } catch (_) {}
    if (debugEnabled && window.console?.info) console.info('[TP Analytics]', name, clean, { shopifyPublished, googlePublished });
    return true;
  };
  window.TPAnalytics = Object.freeze({ track, context: Object.freeze({ ...CONFIG }), debug: debugEnabled });

  const installDataLayerBridge = () => {
    const dataLayer = window.dataLayer = window.dataLayer || [];
    if (dataLayer.__tpBridgeInstalled) return;
    const originalPush = dataLayer.push.bind(dataLayer);
    Object.defineProperty(dataLayer, '__tpBridgeInstalled', { value: true, configurable: false });
    dataLayer.push = function(...items) {
      const result = originalPush(...items);
      items.forEach((item) => {
        if (!item || typeof item !== 'object' || Array.isArray(item) || selfDataLayerObjects.has(item)) return;
        const name = typeof item.event === 'string' ? item.event : '';
        if (!/^tp_[a-z0-9_]{1,36}$/i.test(name)) return;
        const { event, ...rest } = item;
        const clean = sanitize({ ...CONFIG, ...rest });
        if (!isDuplicate(name, clean, 500)) publishShopify(name, clean);
        if (analyticsAllowed() && typeof window.gtag === 'function' && !window.google_tag_manager) {
          try { window.gtag('event', name, clean); } catch (_) {}
        }
        if (debugEnabled && window.console?.info) console.info('[TP Analytics bridge]', name, clean);
      });
      return result;
    };
    dataLayer.slice().forEach((item) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) return;
      const name = typeof item.event === 'string' ? item.event : '';
      if (!/^tp_[a-z0-9_]{1,36}$/i.test(name)) return;
      const { event, ...rest } = item;
      publishShopify(name, sanitize({ ...CONFIG, ...rest }));
    });
  };

  const element = (target) => target instanceof Element ? target : target?.parentElement;
  const closest = (target, selector) => element(target)?.closest(selector) || null;
  const numberFromText = (text) => {
    const match = String(text || '').replace(',', '.').match(/-?\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : undefined;
  };
  const productKey = (text) => {
    const value = normalizeString(text).toLowerCase();
    if (value.includes('pellet')) return 'pellets';
    if (value.includes('nugget')) return 'nuggets';
    if (value.includes('scheib')) return 'scheiben';
    return value ? 'other' : undefined;
  };
  const selectedValue = (root, selector) => {
    const field = root?.querySelector(selector);
    return field && 'value' in field ? normalizeString(field.value) : undefined;
  };
  const selectedMode = (root) => root?.querySelector('[data-pp-mode-button][aria-selected="true"]')?.dataset.ppModeButton || root?.dataset.defaultMode || 'planner';
  const selectedUsecase = (root) => {
    const mode = selectedMode(root);
    if (mode === 'quick') return selectedValue(root, '[data-pp-quick-usecase]');
    if (mode === 'detail') return selectedValue(root, '[data-pp-detail-usecase]');
    return selectedValue(root, '[data-pp-plan-usecase]') || root?.dataset.defaultUsecase;
  };
  const selectedChoice = (root) => {
    const choice = root?.querySelector('[data-pp-plan-choice][aria-checked="true"], [data-pp-plan-choice].is-selected');
    return choice?.dataset.ppChoiceType || choice?.dataset.ppPlanChoice || choice?.dataset.ppChoice || 'recommended';
  };
  const plannerContext = (root) => {
    const resultText = root?.querySelector('[data-pp-result-main]')?.textContent || '';
    const productText = root?.querySelector('[data-pp-product-name]')?.textContent || '';
    return sanitize({
      tp_mode: selectedMode(root),
      tp_usecase: selectedUsecase(root),
      tp_interaction_mode: root?.dataset.interactionMode || 'classic',
      tp_product: productKey(productText),
      tp_result_kg: numberFromText(resultText),
      tp_result_kg_text: normalizeString(resultText, 36),
      tp_duration_hours: numberFromText(selectedValue(root, '[data-pp-plan-duration]')),
      tp_delivery_type: selectedValue(root, '[data-pp-plan-delivery-type]'),
      tp_choice: selectedChoice(root),
      tp_entry_point: CONFIG.tp_page_type || 'storefront'
    });
  };
  const initPlanner = (root) => {
    if (!root || plannerState.has(root)) return;
    const state = { started: false, resultSeen: false, resultSignature: '', lastChoice: '' };
    plannerState.set(root, state);
    const result = root.querySelector('[data-pp-result]');
    if (result) {
      let timer = null;
      const inspectResult = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          if (result.hidden || result.getAttribute('aria-hidden') === 'true') return;
          const context = plannerContext(root), signature = JSON.stringify(context);
          if (!context.tp_result_kg_text || context.tp_result_kg_text === '–' || signature === state.resultSignature) return;
          state.resultSignature = signature;
          if (!state.resultSeen) {
            state.resultSeen = true;
            track('tp_bestellpilot_result', context, { allowRepeat: true });
          } else {
            track('tp_bestellpilot_result_update', context, { allowRepeat: true, dedupeMs: 400 });
          }
        }, 180);
      };
      new MutationObserver(inspectResult).observe(result, { attributes: true, attributeFilter: ['hidden', 'aria-hidden'], childList: true, characterData: true, subtree: true });
      inspectResult();
    }
  };
  const startPlannerIfNeeded = (target) => {
    const root = closest(target, '.prime-planner[data-central-calculator="true"], .prime-planner--central');
    if (!root) return null;
    initPlanner(root);
    const state = plannerState.get(root);
    if (state && !state.started) {
      state.started = true;
      track('tp_bestellpilot_start', plannerContext(root));
    }
    return root;
  };
  const guidedStepNumber = (root) => numberFromText(root?.querySelector('[data-pp-guided-progress]')?.textContent);
  const kgBand = (kg) => {
    const value = Number(kg);
    if (!Number.isFinite(value)) return 'unknown';
    if (value < 20) return '1-19';
    if (value < 50) return '20-49';
    if (value < 100) return '50-99';
    if (value < 250) return '100-249';
    return '250plus';
  };

  const initB2BRoot = (root) => {
    if (!root || b2bState.has(root)) return;
    const state = { started: false, resultSignature: '' };
    b2bState.set(root, state);
    const result = root.querySelector('[data-b2b-result]');
    if (result) {
      const inspect = () => setTimeout(() => {
        if (result.hidden) return;
        const form = root.querySelector('[data-b2b-pilot-form]');
        const payload = sanitize({
          tp_application: selectedValue(form, '[name="pilot_application"]'),
          tp_quantity_band: selectedValue(form, '[name="pilot_quantity"]'),
          tp_frequency: selectedValue(form, '[name="pilot_frequency"]'),
          tp_score: numberFromText(result.querySelector('[data-safety-score]')?.textContent),
          tp_status: normalizeString(result.querySelector('[data-safety-label]')?.textContent, 70),
          tp_product: productKey(result.querySelector('[data-result-product]')?.textContent),
          tp_recommended_kg: numberFromText(result.querySelector('[data-plan-quantity]')?.textContent),
          tp_rhythm: normalizeString(result.querySelector('[data-plan-rhythm]')?.textContent, 70)
        });
        const signature = JSON.stringify(payload);
        if (!signature || signature === state.resultSignature) return;
        state.resultSignature = signature;
        track('tp_b2b_pilot_result', payload, { allowRepeat: true });
      }, 120);
      new MutationObserver(inspect).observe(result, { attributes: true, attributeFilter: ['hidden'], childList: true, subtree: true, characterData: true });
      inspect();
    }
  };
  const initAll = (scope = document) => {
    const planners = scope.matches?.('.prime-planner[data-central-calculator="true"], .prime-planner--central') ? [scope] : scope.querySelectorAll?.('.prime-planner[data-central-calculator="true"], .prime-planner--central');
    planners && Array.from(planners).forEach(initPlanner);
    const b2bRoots = scope.matches?.('[data-b2b-growth]') ? [scope] : scope.querySelectorAll?.('[data-b2b-growth]');
    b2bRoots && Array.from(b2bRoots).forEach(initB2BRoot);
    document.querySelectorAll('.tp-b2b-static__success, .tp-b2b__form-success').forEach((node) => {
      if (node.dataset.tpAnalyticsLead === 'true') return;
      node.dataset.tpAnalyticsLead = 'true';
      track('tp_b2b_lead', { tp_form: node.closest('.tp-b2b-static__form-section') ? 'b2b_contact' : 'b2b_legacy' }, { allowRepeat: true });
    });
  };

  document.addEventListener('focusin', (event) => {
    const planner = startPlannerIfNeeded(event.target);
    if (planner) return;
    const b2bRoot = closest(event.target, '[data-b2b-growth]');
    if (b2bRoot) {
      initB2BRoot(b2bRoot);
      const state = b2bState.get(b2bRoot);
      if (state && !state.started && closest(event.target, '[data-b2b-pilot-form]')) {
        state.started = true;
        track('tp_b2b_pilot_start', { tp_entry_point: 'b2b_pilot' });
      }
    }
    const contactForm = closest(event.target, '.tp-b2b-static__form, .tp-b2b__form');
    if (contactForm && contactForm.dataset.tpAnalyticsStarted !== 'true') {
      contactForm.dataset.tpAnalyticsStarted = 'true';
      track('tp_b2b_start', { tp_form: contactForm.classList.contains('tp-b2b-static__form') ? 'b2b_contact' : 'b2b_legacy' });
    }
  }, true);

  document.addEventListener('click', (event) => {
    const target = element(event.target);
    if (!target) return;
    const planner = startPlannerIfNeeded(target);
    if (planner) {
      const modeButton = closest(target, '[data-pp-mode-button]');
      if (modeButton) setTimeout(() => track('tp_bestellpilot_mode', plannerContext(planner)), 0);
      const next = closest(target, '[data-pp-guided-next], [data-pp-guided-finish]');
      if (next) {
        const before = guidedStepNumber(planner);
        setTimeout(() => {
          const after = guidedStepNumber(planner);
          const finished = Boolean(next.matches('[data-pp-guided-finish]')) || planner.classList.contains('pp-guided-review');
          if (finished) track('tp_bestellpilot_step', { ...plannerContext(planner), tp_completed_step: 4, tp_step_status: 'completed' });
          else if (Number.isFinite(before) && Number.isFinite(after) && after > before) track('tp_bestellpilot_step', { ...plannerContext(planner), tp_completed_step: before, tp_next_step: after });
        }, 30);
      }
      const choice = closest(target, '[data-pp-plan-choice]');
      if (choice) {
        setTimeout(() => {
          const checked = choice.getAttribute('aria-checked') === 'true' || choice.classList.contains('is-selected');
          if (!checked) return;
          const key = choice.dataset.ppChoiceType || choice.dataset.ppPlanChoice || choice.dataset.ppChoice || 'recommended';
          const state = plannerState.get(planner);
          if (state?.lastChoice === key) return;
          if (state) state.lastChoice = key;
          track('tp_bestellpilot_choice', { ...plannerContext(planner), tp_choice: key }, { allowRepeat: true });
        }, 25);
      }
      if (closest(target, '[data-pp-add-to-cart], [data-pp-cart-shortcut]')) track('tp_bestellpilot_cart_click', plannerContext(planner));
      if (closest(target, '[data-pp-product-link]')) track('tp_bestellpilot_product_click', plannerContext(planner));
    }

    const hero = closest(target, '.tpHeroCompare');
    if (hero) {
      if (closest(target, '[data-tp-hero-reorder]')) track('tp_repeat_order', { tp_action: 'reorder_click' });
      else {
        const link = closest(target, 'a[href]');
        if (link) {
          const href = link.getAttribute('href') || '';
          let path;
          if (href === '#bestellpilot' || href.includes('#bestellpilot')) path = 'bestellpilot';
          else if (href.includes('/pages/trockeneis-fuer-unternehmen')) path = 'b2b';
          else if (href.includes('/collections') || href.includes('/products')) path = 'direct_products';
          else if (href.includes('/account')) path = 'account';
          if (path) track('tp_hero_path', { tp_path: path });
        }
      }
    }

    const productCard = closest(target, '.tp-products .tp-card');
    const productLink = productCard && closest(target, 'a[href]');
    if (productCard && productLink) track('tp_direct_product_select', { tp_product: productKey(productCard.querySelector('.tp-card__title')?.textContent), tp_variant_preselected: /[?&]variant=/.test(productLink.getAttribute('href') || '') });

    const feasibility = closest(target, '.tp-feas');
    if (feasibility && closest(target, '[data-transfer]')) {
      let plan = {};
      try { plan = JSON.parse(feasibility.dataset.plan || '{}'); } catch (_) {}
      track('tp_feasibility_transfer', { tp_kg_band: kgBand(plan.kg), tp_same_day: plan.today === true, tp_b2b: Number(plan.kg) >= Number(feasibility.dataset.b2b || 100) });
    }

    const homeB2B = closest(target, '.tpHomeB2b');
    if (homeB2B) {
      const link = closest(target, 'a[href]');
      if (link) {
        const href = link.getAttribute('href') || '';
        track('tp_home_b2b_click', { tp_path: href.includes('#bestellpilot') ? 'bestellpilot' : 'b2b' });
      }
    }

    const b2bRoot = closest(target, '[data-b2b-growth]');
    if (b2bRoot) {
      initB2BRoot(b2bRoot);
      const path = closest(target, '[data-b2b-path]');
      if (path) track('tp_b2b_path_select', { tp_path: path.dataset.b2bPath, tp_frequency: path.dataset.frequency });
      const industry = closest(target, '[data-b2b-industry]');
      if (industry) track('tp_b2b_path_select', { tp_path: 'industry', tp_application: industry.dataset.b2bIndustry, tp_frequency: industry.dataset.frequency });
      const next = closest(target, '[data-v6-next]');
      if (next) {
        const form = b2bRoot.querySelector('[data-b2b-pilot-form]');
        const before = Number(form?.dataset.v6CurrentStep || 1);
        setTimeout(() => {
          const after = Number(form?.dataset.v6CurrentStep || before);
          if (after > before) track('tp_b2b_pilot_step', { tp_completed_step: before, tp_next_step: after });
        }, 40);
      }
      if (closest(target, '[data-b2b-continue]')) track('tp_b2b_pilot_continue', { tp_destination: 'contact' });
      if (closest(target, '[data-b2b-print]')) track('tp_b2b_pilot_print');
    }
  }, false);

  document.addEventListener('submit', (event) => {
    const feasibility = closest(event.target, '.tp-feas');
    if (feasibility && closest(event.target, '[data-form]')) {
      setTimeout(() => {
        const result = feasibility.querySelector('[data-result]');
        if (!result || result.hidden) return;
        let plan = {};
        try { plan = JSON.parse(feasibility.dataset.plan || '{}'); } catch (_) {}
        const status = feasibility.querySelector('[data-status]');
        const state = status?.classList.contains('yellow') ? 'yellow' : status?.classList.contains('blue') ? 'blue' : 'green';
        track('tp_feasibility_check', { tp_status: state, tp_kg_band: kgBand(plan.kg), tp_same_day: plan.today === true, tp_b2b: Number(plan.kg) >= Number(feasibility.dataset.b2b || 100) }, { allowRepeat: true, dedupeMs: 600 });
      }, 0);
    }
    const contactForm = closest(event.target, '.tp-b2b-static__form, .tp-b2b__form');
    if (contactForm) track('tp_b2b_submit', { tp_form: contactForm.classList.contains('tp-b2b-static__form') ? 'b2b_contact' : 'b2b_legacy' }, { allowRepeat: true });
  }, false);

  /* KI-Referrals: nur bekannte KI-Oberflaechen klassifizieren.
     Weder Prompts, URLs, Query-Strings noch personenbezogene Daten werden
     als Eventparameter erfasst. Consent wird vor dem Senden geprueft. */
  const aiSourceFromEntry = () => {
    let host = '';
    try { host = new URL(document.referrer).hostname.toLowerCase(); } catch (_) {}
    let utm = '';
    try { utm = new URLSearchParams(location.search).get('utm_source')?.toLowerCase().trim() || ''; } catch (_) {}
    const map = [
      ['chatgpt', /^(?:chatgpt\.com|chat\.openai\.com)$/, /^(?:chatgpt|chatgpt\.com|openai_chatgpt)$/],
      ['copilot', /^(?:copilot\.microsoft\.com)$/, /^(?:copilot|microsoft_copilot)$/],
      ['perplexity', /^(?:www\.)?perplexity\.ai$/, /^(?:perplexity|perplexity\.ai)$/],
      ['gemini', /^(?:gemini\.google\.com)$/, /^(?:gemini|google_gemini)$/],
      ['claude', /^(?:claude\.ai)$/, /^(?:claude|anthropic_claude)$/]
    ];
    for (const [name, refPattern, utmPattern] of map) {
      if (refPattern.test(host) || utmPattern.test(utm)) return name;
    }
    return '';
  };
  const aiReferralSource = aiSourceFromEntry();
  let aiReferralTracked = false;
  const reportAIReferral = () => {
    if (!aiReferralSource || aiReferralTracked || !analyticsAllowed()) return;
    aiReferralTracked = true;
    track('tp_ai_referral_landing', {
      tp_ai_source: aiReferralSource,
      tp_landing_type: CONFIG.tp_page_type || 'other'
    });
  };
  if (aiReferralSource) {
    document.addEventListener('visitorConsentCollected', reportAIReferral);
    window.addEventListener('visitorConsentCollected', reportAIReferral);
    document.addEventListener('shopify:section:load', reportAIReferral);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', reportAIReferral, { once: true });
    } else {
      reportAIReferral();
    }
  }

  installDataLayerBridge();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  else initAll(document);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  window.addEventListener('pageshow', () => initAll(document));
})();
